import * as React from 'react'

interface DataTableChipProps {
  ariaLabel?: string,
  color?: string,
  icon?: {
    alt: string,
    src: string
  } | JSX.Element,
  label: string | JSX.Element,
  link?: string,
  onClick?: (e: any) => void,
  openInNewTab?: boolean;
}

function renderIcon(icon) {
  if(icon.src) {
    return <img alt={icon.alt} src={icon.src} />
  }
  return icon
}

/**
 * Wraps a DataTableChip and shrink-wraps it to fit the actual content width,
 * even when text wraps to multiple lines. This prevents chips from taking the
 * full cell width when their text wraps.
 *
 * Uses ResizeObserver to react to cell size changes (e.g., column scaling).
 */
export const ShrinkWrapChip: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const lastCellWidthRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!wrapperRef.current) return;

    const cell = wrapperRef.current.closest('.data-table-row-section') as HTMLElement;
    if (!cell) return;

    const adjustWidth = () => {
      if (!wrapperRef.current) return;

      const chip = wrapperRef.current.querySelector('.data-table-chip') as HTMLElement;
      if (!chip) return;

      const availableWidth = cell.getBoundingClientRect().width - 16; // 16px for cell padding

      // Skip if cell width hasn't changed
      if (lastCellWidthRef.current === availableWidth) return;
      lastCellWidthRef.current = availableWidth;

      // Measure natural content width without constraints. Use the fractional
      // bounding-rect width rounded up.
      chip.style.width = 'max-content';
      chip.style.maxWidth = 'none';
      const contentWidth = Math.ceil(chip.getBoundingClientRect().width);

      // If content fits without wrapping, use content width
      if (contentWidth <= availableWidth) {
        chip.style.width = `${contentWidth}px`;
        chip.style.maxWidth = '100%';
        return;
      }

      // Content needs to wrap - measure wrapped height at available width
      chip.style.maxWidth = '100%';
      chip.style.width = `${availableWidth}px`;
      const wrappedHeight = chip.offsetHeight;

      // Get min-content width (longest word) as the floor
      chip.style.width = 'min-content';
      chip.style.maxWidth = 'none';
      const minContentWidth = chip.offsetWidth;
      chip.style.maxWidth = '100%';

      // Binary search for minimum width that maintains the same wrapped height
      let low = minContentWidth;
      let high = availableWidth;

      while (high - low > 1) {
        const mid = Math.floor((low + high) / 2);
        chip.style.width = `${mid}px`;
        if (chip.offsetHeight > wrappedHeight) {
          low = mid;
        } else {
          high = mid;
        }
      }

      chip.style.width = `${high}px`;
    };

    const resizeObserver = new ResizeObserver(adjustWidth);
    resizeObserver.observe(cell);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <span ref={wrapperRef} style={{ display: 'inline-block', maxWidth: '100%' }}>
      {children}
    </span>
  );
};

export const DataTableChip = ({ ariaLabel, color, icon, label, link, onClick, openInNewTab, }: DataTableChipProps) => {
  if(link) {
    const anchorProps = {
      href: link,
      className: `data-table-chip ${color} focus-on-light`
    }

    if (openInNewTab) {
      anchorProps['target'] = '_blank'
      anchorProps['rel'] = 'noopener noreferrer'
    }

    return(
      <a {...anchorProps}>
        {icon && renderIcon(icon)}
        <span>{label}</span>
      </a>
    )
  }
  if (onClick) {
    return (
      <button aria-label={ariaLabel} className={`data-table-chip ${color} focus-on-light`} onClick={onClick} type="submit">
        {icon && renderIcon(icon)}
        <span>{label}</span>
      </button>
    )
  }
  return (
    <div aria-label={ariaLabel} className={`data-table-chip ${color}`}>
      {icon && renderIcon(icon)}
      <span>{label}</span>
    </div>
  )
}

export default DataTableChip
